<template>
  <div class="grid is-col-min-15"
    :class="{ 'quote-rates-licence-expired': $licenceExpired }">
    <div class="cell is-flex is-flex-direction-column"
      v-if="innerValue">
      <article class="box is-flex-grow-1">
        <p class="title">Rates</p>
        <div v-if="isNtar"
          class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Shop Rate</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.shopRate }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.shopRate"
                    :min="0"
                    placeholder="Shop Rate"
                    :precision="2"
                    v-focus-inserted
                    @focus="captureFieldSnapshot('shopRate')"
                    @blur="handleFieldBlur('shopRate')"
                    :disabled="isReadOnly || savingFields.shopRate" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!isNtar">
          <div v-for="(codeType) in labourCodeTypes"
            :key="codeType.order"
            class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">{{ getLabourColumnName(codeType.code) }}</label>
            </div>
            <div class="field-body">
              <div class="field">
                <p class="control"
                  :class="{ 'is-loading': ratesSaving[codeType.code] }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="getLabourRate(codeType.code).rate"
                    :min="0"
                    :placeholder="getLabourColumnName(codeType.code)"
                    :precision="2"
                    @focus="captureRateSnapshot(codeType.code)"
                    @blur="handleRateBlur(codeType.code)"
                    :disabled="isReadOnly || ratesSaving[codeType.code]" />
                </p>
              </div>
            </div>
          </div>
          <div v-for="(codeType) in otherLabourCodeTypes"
            :key="codeType.order"
            class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">{{ getLabourColumnName(codeType.code) }}</label>
            </div>
            <div class="field-body">
              <div class="field">
                <p class="control"
                  :class="{ 'is-loading': ratesSaving[codeType.code] }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="getLabourRate(codeType.code).rate"
                    :min="0"
                    :placeholder="getLabourColumnName(codeType.code)"
                    :precision="2"
                    @focus="captureRateSnapshot(codeType.code)"
                    @blur="handleRateBlur(codeType.code)"
                    :disabled="isReadOnly || ratesSaving[codeType.code]" />
                </p>
              </div>
            </div>
          </div>
          <div v-if="isRacq"
            class="field is-horizontal">
            <div class="field-body">
              <div class="field-label">
                <label class="label">RACQ Cons.</label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control"
                    :class="{ 'is-loading': savingFields.paintCons }">
                    <vue-numeric
                      class="input has-text-right"
                      v-model="innerValue.paintCons"
                      :min="0"
                      placeholder="RACQ Cons."
                      :precision="2"
                      @focus="captureFieldSnapshot('paintCons')"
                      @blur="handleFieldBlur('paintCons')"
                      :disabled="isReadOnly || savingFields.paintCons" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <div v-if="!isNtar">
        <article class="box">
          <p class="title">Paint Rates</p>
          <div class="columns">
            <div class="column">
              <div
                v-for="(codeType, index) in paintCodeTypes.slice(0, Math.ceil(paintCodeTypes.length / 2))"
                :key="index"
                class="field is-horizontal">
                <div class="field-label is-normal">
                  <label
                    v-if="vehicle"
                    class="label"
                    :class="{ 'has-text-success' : codeType.code === vehicle.paintGroup }">{{ getLabourColumnName(codeType.code) }}</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <p class="control"
                      :class="{ 'is-loading': ratesSaving[codeType.code] }">
                      <vue-numeric
                        class="input has-text-right"
                        v-model="getLabourRate(codeType.code).rate"
                        :min="0"
                        :placeholder="getLabourColumnName(codeType.code)"
                        :precision="2"
                        @blur="handleRateBlur(codeType.code)"
                        :disabled="isReadOnly || ratesSaving[codeType.code]" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="column">
              <div
                v-for="(codeType, index) in paintCodeTypes.slice(Math.ceil(paintCodeTypes.length / 2), paintCodeTypes.length)"
                :key="index"
                class="field is-horizontal">
                <div
                  v-if="vehicle"
                  class="field-label is-normal">
                  <label
                    class="label"
                    :class="{ 'has-text-success' : codeType.code === vehicle.paintGroup }">{{ getLabourColumnName(codeType.code) }}</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <p class="control"
                      :class="{ 'is-loading': ratesSaving[codeType.code] }">
                      <vue-numeric
                        class="input has-text-right"
                        v-model="getLabourRate(codeType.code).rate"
                        :min="0"
                        :placeholder="getLabourColumnName(codeType.code)"
                        :precision="2"
                        @blur="handleRateBlur(codeType.code)"
                        :disabled="isReadOnly || ratesSaving[codeType.code]" />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
    <div class="cell is-flex">
      <article class="box is-flex-grow-1">
        <p class="title">Markups</p>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">New</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.newMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.newMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="New Markup %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('newMarkup')"
                    @blur="handleFieldBlur('newMarkup')"
                    :disabled="isReadOnly || savingFields.newMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Parallel</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control parallel-markup-control"
                  :class="{ 'is-loading': savingFields.parallelMarkup }">
                  <span class="tag parallel-markup-new-badge">NEW</span>
                  <vue-numeric
                    class="input has-text-right parallel-markup-input"
                    v-model="innerValue.parallelMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Parallel %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('parallelMarkup')"
                    @blur="handleFieldBlur('parallelMarkup')"
                    :disabled="isReadOnly || savingFields.parallelMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Used</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.usedMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.usedMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Used Markup %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('usedMarkup')"
                    @blur="handleFieldBlur('usedMarkup')"
                    :disabled="isReadOnly || savingFields.usedMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Recore</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.recoreMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.recoreMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Recore %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('recoreMarkup')"
                    @blur="handleFieldBlur('recoreMarkup')"
                    :disabled="isReadOnly || savingFields.recoreMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Aftermarket</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.afterMarketMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.afterMarketMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Aftermarket %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('afterMarketMarkup')"
                    @blur="handleFieldBlur('afterMarketMarkup')"
                    :disabled="isReadOnly || savingFields.afterMarketMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Exchange</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.exchangeMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.exchangeMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Exchange %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('exchangeMarkup')"
                    @blur="handleFieldBlur('exchangeMarkup')"
                    :disabled="isReadOnly || savingFields.exchangeMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-body">
            <div class="field-label is-normal">
              <label class="label">Reconditioned</label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control"
                  :class="{ 'is-loading': savingFields.reconditionedMarkup }">
                  <vue-numeric
                    class="input has-text-right"
                    v-model="innerValue.reconditionedMarkup"
                    :min="0"
                    :max="100"
                    symbol="%"
                    placeholder="Reconditioned %"
                    symbol-position="suffix"
                    :precision="2"
                    @focus="captureFieldSnapshot('reconditionedMarkup')"
                    @blur="handleFieldBlur('reconditionedMarkup')"
                    :disabled="isReadOnly || savingFields.reconditionedMarkup" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
    <totals-floating-widget
      :active.sync="isFloatingWidgetActive"
      title="Totals"
      :rr-total="rrTotal"
      :repair-total="repairTotal"
      :rwa-total="rwaTotal"
      :paint-total="paintTotal"
      :cd-total="cdTotal"
      :crush-total="crushTotal"
      :mech-total="mechTotal"
      :fg-total="fgTotal"
      :part-total="partTotal"
      :opg-total="opgTotal"
      :total-ex-gst="innerValue.totalExGst" />
  </div>
</template>

<script>
import { LabourTimeTypes, LabourItemCodeTypes, ItemCategoryTypes, QuotingMethodTypes, ItemCodeTypes, PartTypes, EventHubTypes } from '@/enums'
import VueNumeric from '@/components/VueNumeric'
import { FocusInserted } from '@/components/directives'
import { roundAwayFromZero } from '@/components/utils/AccountingFunctions'
import _orderBy from 'lodash/orderBy'
import QuoteService from './QuoteService'
import QuoteAutoSaveService from './QuoteAutoSaveService'
import { TotalsFloatingWidget } from './components'
import { QuoteLabourRateModel } from '@/classes/viewmodels'
import { QuoteLabourMixin, QuoteOtherLabourMixin, QuoteOpgMixin, QuotePartMixin } from './mixins'

export default {
  name: 'QuoteRatesMarkups',
  components: {
    VueNumeric,
    TotalsFloatingWidget
  },
  directives: {
    FocusInserted
  },
  mixins: [QuoteLabourMixin, QuoteOtherLabourMixin, QuoteOpgMixin, QuotePartMixin],
  props: {
    value: null,
    vehicle: null,
    readOnlyView: {
      type: Boolean,
      default: false
    },
    autoSaveEnabled: { type: Boolean, default: false }
  },
  data() {
    return {
      innerValue: null,
      // vehicle: null,
      isFloatingWidgetActive: true,
      savingFields: {
        shopRate: false,
        newMarkup: false,
        usedMarkup: false,
        recoreMarkup: false,
        afterMarketMarkup: false,
        exchangeMarkup: false,
        reconditionedMarkup: false,
        parallelMarkup: false,
        paintCons: false
      },
      ratesSaving: {},
      fieldSnapshots: {},
      rateSnapshots: {},
      draftRates: {}
    }
  },
  computed: {
    isReadOnly() {
      return this.innerValue.readOnly || this.readOnlyView || this.$licenceExpired
    },
    labourCodeEnum: function() {
      return LabourItemCodeTypes
    },
    itemCodeTypes() {
      return ItemCodeTypes
    },
    partTypes() {
      return PartTypes
    },
    isNtar() {
      return (
        this.innerValue &&
        (this.innerValue.labourType === LabourTimeTypes.NTAR ||
          this.innerValue.labourType === LabourTimeTypes.LTAR ||
          this.innerValue.labourType === LabourTimeTypes.eMTA)
      )
    },
    isRacq() {
      return this.innerValue && this.innerValue.labourType === LabourTimeTypes.RACQ
    },
    paintCodeTypes() {
      const types = _orderBy(Object.values(ItemCodeTypes).filter(v => v.type === 'paint'), 'order')
      return types
    },
    labourCodeTypes() {
      const types = _orderBy(Object.values(ItemCodeTypes).filter(v => v.type === 'labour'), 'order')
      const filteredTypes = []
      for (const type of types) {
        if (this.innerValue.rates.some(r => r.labourCodeId === type.code)) {
          const labourType = this.$labourTypes && this.$labourTypes.find(t => t.labourTypeCode === type.code)
          if (labourType && labourType.active && labourType.enabled) {
            filteredTypes.push(type)
          }
        }
      }
      return filteredTypes
    },
    otherLabourCodeTypes() {
      const types = _orderBy(Object.values(ItemCodeTypes).filter(v => v.type === 'otherlabour'), 'order')
      const filteredTypes = []
      for (const type of types) {
        if (this.innerValue.rates.some(r => r.labourCodeId === type.code)) {
          const labourType = this.$labourTypes && this.$labourTypes.find(t => t.labourTypeCode === type.code)
          if (labourType && labourType.active && labourType.enabled) {
            filteredTypes.push(type)
          }
        }
      }
      return filteredTypes
    },
    rrRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.RR.code) : null
      return rate ? rate.rate : null
    },
    repairRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.REP.code) : null
      return rate ? rate.rate : null
    },
    rwaRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.RWA.code) : null
      return rate ? rate.rate : null
    },
    mechRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.MECH.code) : null
      return rate ? rate.rate : null
    },
    cdRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.CD.code) : null
      return rate ? rate.rate : null
    },
    crushRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.CRUSH.code) : null
      return rate ? rate.rate : null
    },
    fgRate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.FIBER.code) : null
      return rate ? rate.rate : null
    },
    s1Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.S1.code) : null
      return rate ? rate.rate : null
    },
    s2Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.S2.code) : null
      return rate ? rate.rate : null
    },
    s3Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.S3.code) : null
      return rate ? rate.rate : null
    },
    m1Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.M1.code) : null
      return rate ? rate.rate : null
    },
    m2Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.M2.code) : null
      return rate ? rate.rate : null
    },
    m3Rate() {
      const rate = this.innerValue ? this.innerValue.rates.find(r => r.labourCodeId === ItemCodeTypes.M3.code) : null
      return rate ? rate.rate : null
    }
  },
  watch: {
    innerValue: {
      handler: function(newVal, oldVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    rrTotal(newVal) {
      this.innerValue.rrTotal = this.rrTotal
    },
    repairTotal(newVal) {
      this.innerValue.repairTotal = this.repairTotal
    },
    rwaTotal(newVal) {
      this.innerValue.rwaTotal = this.rwaTotal
    },
    cdTotal(newVal) {
      this.innerValue.cdTotal = this.cdTotal
    },
    crushTotal(newVal) {
      this.innerValue.crushTotal = this.crushTotal
    },
    mechTotal(newVal) {
      this.innerValue.mechTotal = this.mechTotal
    },
    fgTotal(newVal) {
      this.innerValue.fgTotal = this.fgTotal
    },
    rrRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesLabourTotal(ItemCodeTypes.RR.code)
      }
    },
    repairRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesLabourTotal(ItemCodeTypes.REP.code)
      }
    },
    rwaRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesLabourTotal(ItemCodeTypes.RWA.code)
      }
    },
    mechRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesOtherLabourTotal(ItemCodeTypes.MECH.code)
      }
    },
    cdRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesOtherLabourTotal(ItemCodeTypes.CD.code)
      }
    },
    crushRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesOtherLabourTotal(ItemCodeTypes.CRUSH.code)
      }
    },
    fgRate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesOtherLabourTotal(ItemCodeTypes.FIBER.code)
      }
    },
    s1Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.S1.code)
      }
    },
    s2Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.S2.code)
      }
    },
    s3Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.S3.code)
      }
    },
    m1Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.M1.code)
      }
    },
    m2Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.M2.code)
      }
    },
    m3Rate(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPaintTotal(ItemCodeTypes.M3.code)
      }
    },
    'innerValue.newMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.NEW, oldVal)
      }
    },
    'innerValue.usedMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.USED, oldVal)
      }
    },
    'innerValue.recordMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.RECR, oldVal)
      }
    },
    'innerValue.afterMarketMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.AFT, oldVal)
      }
    },
    'innerValue.exchangeMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.EXCH, oldVal)
      }
    },
    'innerValue.reconditionedMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.RECD, oldVal)
      }
    },
    'innerValue.parallelMarkup': function(newVal, oldVal) {
      if (oldVal) {
        this.recalculatesPartTotal(PartTypes.PAR, oldVal)
      }
    },
    'innerValue.shopRate': function(newVal, oldVal) {
      if (oldVal && this.innerValue.rates && this.isNtar) {
        this.innerValue.rates.forEach(function(item, index) {
          item.rate = newVal
        })
        this.recalculatesOtherLabourTotal()
        this.recalculatesLabourTotal()
        this.recalculatesOpgTotal()
        this.recalculatesPaintTotal(this.vehicle.paintGroup)
      }
    }
  },
  created() {
    this.innerValue = this.value
    this.initDraftRates()
  },
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, () => {
      this.innerValue = this.value
      this.initDraftRates()
    })
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
  },
  methods: {
    buildRatesMarkupsPayload() {
      return {
        quoteId: this.innerValue.quoteId,
        newMarkup: this.innerValue.newMarkup || 0,
        usedMarkup: this.innerValue.usedMarkup || 0,
        recoreMarkup: this.innerValue.recoreMarkup || 0,
        afterMarketMarkup: this.innerValue.afterMarketMarkup || 0,
        exchangeMarkup: this.innerValue.exchangeMarkup || 0,
        reconditionedMarkup: this.innerValue.reconditionedMarkup || 0,
        parallelMarkup: this.innerValue.parallelMarkup || 0,
        rates: this.innerValue.rates || []
      }
    },
    captureFieldSnapshot(fieldKey) {
      this.$set(this.fieldSnapshots, fieldKey, this.innerValue[fieldKey])
    },
    captureRateSnapshot(rateCode) {
      // via getLabourRate so a not-yet-promoted draft is captured too
      this.$set(this.rateSnapshots, rateCode, this.getLabourRate(rateCode).rate ?? null)
    },
    async handleFieldBlur(fieldKey) {
      if (this.isReadOnly) return
      if (!this.autoSaveEnabled) return
      if (this.innerValue[fieldKey] === this.fieldSnapshots[fieldKey]) return
      this.$set(this.savingFields, fieldKey, true)
      this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, { isSaving: true })
      try {
        await QuoteAutoSaveService.saveRatesMarkups(this.innerValue.quoteId, this.buildRatesMarkupsPayload())
        this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, { isSaving: false, isFailed: false })
        this.$eventHub.$emit(EventHubTypes.AutoSaveQuoteItems)
      } catch (e) {
        this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, {
          isSaving: false,
          isFailed: true,
          failMessage: e?.message || 'Save failed'
        })
      } finally {
        this.$set(this.savingFields, fieldKey, false)
      }
    },
    async handleRateBlur(rateCode) {
      if (this.isReadOnly) return
      const rateModel = this.getLabourRate(rateCode)
      const currentRate = rateModel.rate ?? null
      if (currentRate === this.rateSnapshots[rateCode]) return
      // Promote the draft so both manual save and autosave see it.
      // Must run before the autosave gate below.
      if (!this.innerValue.rates.some(r => r.labourCodeId === rateCode)) {
        this.innerValue.rates.push(rateModel)
      }
      if (!this.autoSaveEnabled) return
      this.$set(this.ratesSaving, rateCode, true)
      this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, { isSaving: true })
      try {
        await QuoteAutoSaveService.saveRatesMarkups(this.innerValue.quoteId, this.buildRatesMarkupsPayload())
        this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, { isSaving: false, isFailed: false })
        this.$eventHub.$emit(EventHubTypes.AutoSaveQuoteItems)
      } catch (e) {
        this.$eventHub.$emit(EventHubTypes.AutoSaveRatesMarkupsStatus, {
          isSaving: false,
          isFailed: true,
          failMessage: e?.message || 'Save failed'
        })
      } finally {
        this.$set(this.ratesSaving, rateCode, false)
      }
    },
    getLabourRate(code) {
      if (!this.innerValue.rates) {
        return {}
      }
      const existing = this.innerValue.rates.find(i => i.labourCodeId === code)
      if (existing) {
        return existing
      }
      // Unsaved rates stay as local drafts; promoted on edit (see handleRateBlur).
      if (!this.draftRates[code]) {
        const labourType = this.$labourTypes.find(t => t.labourTypeCode === code) || this.$labourTypes.find(t => t.labourTypeCode === 'PAINT')
        this.$set(this.draftRates, code, new QuoteLabourRateModel(this.innerValue.quoteId, code, labourType.labourTypeId, this.isNtar ? this.innerValue.shopRate : 0))
      }
      return this.draftRates[code]
    },
    initDraftRates() {
      // Pre-build paint-code drafts so the first render is side-effect free.
      this.draftRates = {}
      if (!this.innerValue || !this.innerValue.rates || !this.$labourTypes) {
        return
      }
      for (const codeType of this.paintCodeTypes) {
        const code = codeType.code
        if (this.innerValue.rates.some(r => r.labourCodeId === code)) {
          continue
        }
        const labourType = this.$labourTypes.find(t => t.labourTypeCode === code) || this.$labourTypes.find(t => t.labourTypeCode === 'PAINT')
        if (labourType) {
          this.$set(this.draftRates, code, new QuoteLabourRateModel(this.innerValue.quoteId, code, labourType.labourTypeId, this.isNtar ? this.innerValue.shopRate : 0))
        }
      }
    },
    getLabourColumnName(code) {
      if (!this.innerValue.rates) {
        return ''
      }
      var rate = this.innerValue.rates.find(i => i.labourCodeId === code)
      // console.log(rate)
      return rate ? rate.labourCodeId : code
    },
    async getVehicleEntity() {
      this.vehicle = await QuoteService.getVehicleEntity(this.innerValue.vehicleId)
    },
    getRate(code) {
      const rate = !this.isNtar ? this.innerValue.rates.find(i => i.labourCodeId === code).rate : this.innerValue.shopRate
      return rate
    },
    recalculatesPaintTotal(code) {
      if (this.innerValue && this.vehicle && code === this.vehicle.paintGroup) {
        console.log('recalculatesPaintTotal')
        const items = this.innerValue.labours.filter(i => !i.deleted && i.itemType === ItemCategoryTypes.PAINT)
        const rate = this.getRate(code)
        const vm = this
        items.forEach(function(item) {
          item.rate = item.rev !== '1' ? rate : item.rate
          if (vm.innerValue.quotingMethod === QuotingMethodTypes.Hour) {
            item.dollarValue = roundAwayFromZero(item.rate * item.hourValue)
          } else {
            item.dollarValue = item.hourValue
          }
        })
        this.innerValue.paintTotal = this.paintTotal
        this.isFloatingWidgetActive = true
      }
      this.recalculatesOpgTotal(code)
    },
    recalculatesOpgTotal(code) {
      if (this.innerValue) {
        console.log('recalculatesOpgTotal')
        const items = code
          ? this.innerValue.opgs.filter(i => !i.deleted && i.itemType === ItemCategoryTypes.OPG && i.opgCode === code)
          : this.innerValue.opgs.filter(i => !i.deleted && i.itemType === ItemCategoryTypes.OPG)
        const rate = this.getRate(code)
        const vm = this
        items.forEach(function(item) {
          item.rate = item.rev !== '1' ? rate : item.rate
          if (vm.innerValue.quotingMethod === QuotingMethodTypes.Hour) {
            item.dollarValue = roundAwayFromZero(item.rate * item.hourValue)
          } else {
            item.dollarValue = item.hourValue
          }
        })
        this.innerValue.opgTotal = this.opgTotal
        this.isFloatingWidgetActive = true
      }
    },
    recalculatesLabourTotal(code) {
      if (this.innerValue) {
        console.log('recalculatesLabourTotal')
        const items = code ? this.innerValue.labours.filter(i => !i.deleted && i.itemType === code) : this.innerValue.labours.filter(i => !i.deleted)
        const rate = this.getRate(code)
        const vm = this
        items.forEach(function(item) {
          item.rate = item.rev !== '1' ? rate : item.rate
          if (vm.innerValue.quotingMethod === QuotingMethodTypes.Hour) {
            item.dollarValue = roundAwayFromZero(item.rate * item.hourValue)
          } else {
            item.dollarValue = item.hourValue
          }
        })
        this.isFloatingWidgetActive = true
      }
    },
    recalculatesOtherLabourTotal(code) {
      if (this.innerValue) {
        console.log('recalculatesOtherLabourTotal')
        const items = code ? this.innerValue.others.filter(i => !i.deleted && i.itemType === code) : this.innerValue.others.filter(i => !i.deleted)
        const rate = this.getRate(code)
        const vm = this
        items.forEach(function(item) {
          item.rate = item.rev !== '1' ? rate : item.rate
          if (vm.innerValue.quotingMethod === QuotingMethodTypes.Hour) {
            item.dollarValue = roundAwayFromZero(item.rate * item.hourValue)
          } else {
            item.dollarValue = item.hourValue
          }
        })
        this.isFloatingWidgetActive = true
      }
    },
    recalculatesPartTotal(partType, oldMarkupPercent) {
      // debounce because if statement is checking reactive properties "item.markupPercent"
      if (this.innerValue) {
        console.log('recalculatesPartTotal')
        const items = this.innerValue.parts.filter(i => !i.deleted && i.mark === partType.value)
        let markup = 0
        switch (partType.value) {
          case PartTypes.NEW.value:
            markup = this.innerValue.newMarkup
            break
          case PartTypes.USED.value:
            markup = this.innerValue.usedMarkup
            break
          case PartTypes.EXCH.value:
            markup = this.innerValue.exchangeMarkup
            break
          case PartTypes.RECR.value:
            markup = this.innerValue.recoreMarkup
            break
          case PartTypes.RECD.value:
            markup = this.innerValue.reconditionedMarkup
            break
          case PartTypes.AFT.value:
            markup = this.innerValue.afterMarketMarkup
            break
          case PartTypes.PAR.value:
            markup = this.innerValue.parallelMarkup
            break
        }
        // console.log(markup)
        items.forEach(function(item) {
          if (item.markupPercent === 0 || item.markupPercent === oldMarkupPercent) {
            item.markupPercent = markup
            item.markupValue = roundAwayFromZero(item.value * (1 + markup / 100))
          }
        })
        this.innerValue.partTotal = this.partTotal
        this.isFloatingWidgetActive = true
      }
    }
  }
}
</script>

<style lang="scss">
.parallel-markup-control {
  position: relative;
}
.parallel-markup-control .tag.parallel-markup-new-badge {
  position: absolute;
  top: -0.7rem;
  right: -0.3rem;
  z-index: 2;
  height: 1.25rem;
  padding: 0 0.4rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #1a1a1a;
  background-color: #f0a93b;
}
.parallel-markup-control .input.parallel-markup-input {
  border-color: #f0a93b;
  box-shadow: 0 0 0 1px rgba(240, 167, 58, 0.5);
}

.quote-rates-licence-expired .control:has(> input:disabled) {
  cursor: not-allowed;

  &:hover::before {
    content: 'Disabled, as your licence has expired';
    position: absolute;
    top: 100%;
    left: 0;
    transform: translateY(0.5rem);
    z-index: 30;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    background: rgba(74, 74, 74, 0.9);
    color: #fff;
    font-size: 0.75rem;
    font-weight: normal;
    white-space: nowrap;
    pointer-events: none;
  }
}
</style>
